import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Loader2, Trash2, Cloud, AlertCircle } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUploader = ({
  value = '',
  onChange,
  folder = 'general',
  label = 'Imagen',
  multiple = false,
  gallery = [],
  onGalleryChange,
  maxFiles = 10,
  aspectRatio = null,
  previewHeight = 200,
  className = ''
}) => {
  const { token } = useAdminAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const uploadToCloudinary = async (file) => {
    // Upload directly through our backend (simpler and more secure)
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const response = await fetch(`${API_URL}/api/admin/cloudinary/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Error al subir imagen');
    }

    const data = await response.json();
    return data.image.url;
  };

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;

    setError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      if (multiple) {
        const newUrls = [];
        const totalFiles = Math.min(files.length, maxFiles - gallery.length);
        
        for (let i = 0; i < totalFiles; i++) {
          const url = await uploadToCloudinary(files[i]);
          newUrls.push(url);
          setUploadProgress(((i + 1) / totalFiles) * 100);
        }

        if (onGalleryChange) {
          onGalleryChange([...gallery, ...newUrls]);
        }
        toast.success(`${newUrls.length} imagen(es) subida(s) a la nube`);
      } else {
        const url = await uploadToCloudinary(files[0]);
        setUploadProgress(100);
        
        if (onChange) {
          onChange(url);
        }
        toast.success('Imagen subida a Cloudinary');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    handleFiles(Array.from(files));
  }, [gallery, multiple]);

  const handleInputChange = (e) => {
    const files = e.target.files;
    handleFiles(Array.from(files));
    e.target.value = '';
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    if (onChange) {
      onChange('');
    }
  };

  const removeFromGallery = (index) => {
    if (onGalleryChange) {
      const newGallery = [...gallery];
      newGallery.splice(index, 1);
      onGalleryChange(newGallery);
    }
  };

  const aspectRatioClass = aspectRatio ? {
    '1:1': 'aspect-square',
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
    '3:4': 'aspect-[3/4]'
  }[aspectRatio] : '';

  const isCloudinaryUrl = (url) => url && url.includes('cloudinary.com');

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
        {label}
        <Cloud size={14} className="text-amber-500" title="Almacenamiento en la nube" />
      </label>

      {/* Single Image Preview */}
      {!multiple && value && (
        <div className="relative mb-4 group">
          <div 
            className={`relative overflow-hidden rounded-lg bg-gray-700 ${aspectRatioClass}`}
            style={!aspectRatioClass ? { height: previewHeight } : {}}
          >
            <img 
              src={value} 
              alt="Preview" 
              className="w-full h-full object-cover"
            />
            {isCloudinaryUrl(value) && (
              <div className="absolute top-2 left-2 bg-amber-500/90 px-2 py-1 rounded text-xs text-black font-medium flex items-center gap-1">
                <Cloud size={12} /> Cloudinary
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={handleClick}
                className="p-2 bg-amber-500 rounded-lg text-black hover:bg-amber-600 transition-colors"
                title="Cambiar imagen"
              >
                <Upload size={20} />
              </button>
              <button
                type="button"
                onClick={removeImage}
                className="p-2 bg-red-500 rounded-lg text-white hover:bg-red-600 transition-colors"
                title="Eliminar"
              >
                <Trash2 size={20} />
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2 truncate">{value}</p>
        </div>
      )}

      {/* Multiple Images Gallery */}
      {multiple && gallery.length > 0 && (
        <div className="grid grid-cols-4 gap-3 mb-4">
          {gallery.map((url, index) => (
            <div key={index} className="relative group">
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-700">
                <img 
                  src={url} 
                  alt={`Gallery ${index + 1}`} 
                  className="w-full h-full object-cover"
                />
                {isCloudinaryUrl(url) && (
                  <div className="absolute top-1 left-1">
                    <Cloud size={14} className="text-amber-500 drop-shadow-lg" />
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeFromGallery(index)}
                className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Zone */}
      {(!value || multiple) && (multiple ? gallery.length < maxFiles : true) && (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
            ${isDragging 
              ? 'border-amber-500 bg-amber-500/10' 
              : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/30'
            }
            ${error ? 'border-red-500/50' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple={multiple}
            onChange={handleInputChange}
            className="hidden"
            data-testid="image-upload-input"
          />

          {isUploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="text-amber-500 animate-spin" size={40} />
              <div className="w-48 h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-400">Subiendo a Cloudinary... {Math.round(uploadProgress)}%</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className={`p-4 rounded-full ${isDragging ? 'bg-amber-500/20' : 'bg-gray-700'}`}>
                <Cloud className={isDragging ? 'text-amber-500' : 'text-gray-400'} size={32} />
              </div>
              <div>
                <p className="text-sm text-white font-medium">
                  {isDragging ? 'Suelta la imagen aquí' : 'Arrastra una imagen o haz clic para seleccionar'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, PNG, WebP o GIF. Máximo 10MB. <span className="text-amber-500">Almacenamiento permanente en la nube</span>
                </p>
              </div>
              {multiple && (
                <p className="text-xs text-gray-500">
                  {gallery.length} / {maxFiles} imágenes
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 flex items-center justify-center gap-2 text-red-400">
              <AlertCircle size={16} />
              <span className="text-sm">{error}</span>
            </div>
          )}
        </div>
      )}

      {/* URL Input (fallback) */}
      {!multiple && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 h-px bg-gray-700" />
            <span className="text-xs text-gray-500">o ingresa URL</span>
            <div className="flex-1 h-px bg-gray-700" />
          </div>
          <input
            type="url"
            value={value}
            onChange={(e) => onChange && onChange(e.target.value)}
            placeholder="https://..."
            className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 text-sm"
            data-testid="image-url-input"
          />
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
